import apiClient from './apiClient'
import { OCR_SECTION_PROCESS, OCR_PROCESS } from './apiEndpoints'
import { log } from '@/utils/logger'
import type { ApiResponse } from '@/types'

export interface OcrFieldEntry {
  value: unknown
  source: string
  confidence?: number
}

export interface OcrKeyValuePair {
  key: string
  value: string
  confidence: number
  _source?: 'prebuilt' | 'custom'
}

export interface OcrSection {
  section: string
  fields: Record<string, OcrFieldEntry>
}

export interface OcrResult {
  sections: OcrSection[]
  keyValuePairs: OcrKeyValuePair[]
  uploadedName?: string
  uploadedSection?: string
}

/** Minimum confidence (exclusive) to accept an OCR-mapped field. Change here to reconfigure. */
export const OCR_CONFIDENCE_THRESHOLD = 0.5

const IGNORED_OCR_VALUES = new Set(['', 'na', 'n/a', 'null', 'unknown'])

/** Normalise Azure Document Intelligence checkbox markers to human-readable values.
 *  Returns 'NA' for any value that should be ignored (null, empty, N/A, unknown). */
export function normaliseOcrValue(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim()
  if (trimmed === ':selected:') return 'Yes'
  if (trimmed === ':unselected:') return 'No'
  if (IGNORED_OCR_VALUES.has(trimmed.toLowerCase())) return 'NA'
  return trimmed
}

export async function processOcrSection(
  userId: string,
  caseId: string,
  sectionId: string,
): Promise<ApiResponse<OcrResult>> {
  log.info('processOcrSection start', { userId, caseId, sectionId })
  try {
    const { data } = await apiClient.post<ApiResponse<OcrResult>>(
      OCR_SECTION_PROCESS,
      { userId, caseId, sectionId },
      { timeout: 180_000 },
    )
    log.info('processOcrSection success', { userId, caseId, sectionId, success: String(data.success) })
    return data
  } catch (err) {
    log.error('processOcrSection failed', err instanceof Error ? err : new Error(String(err)), { userId, caseId, sectionId })
    throw err
  }
}

export async function processOcrImage(
  file: File | null,
  userId: string,
  caseId: string,
  sectionId: string,
  blobPath?: string,
): Promise<ApiResponse<OcrResult>> {
  if (!file && !blobPath) throw new Error('Either a file or blobPath is required')

  log.info('processOcrImage start', { userId, caseId, sectionId, source: file ? 'file' : 'blob' })

  const formData = new FormData()
  if (file) formData.append('image', file)
  if (blobPath) formData.append('blobPath', blobPath)
  formData.append('userId', userId)
  formData.append('caseId', caseId)
  formData.append('sectionId', sectionId)

  try {
    const { data } = await apiClient.post<ApiResponse<OcrResult>>(OCR_PROCESS, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    })
    log.info('processOcrImage success', { userId, caseId, sectionId, success: String(data.success) })
    return data
  } catch (err) {
    log.error('processOcrImage failed', err instanceof Error ? err : new Error(String(err)), { userId, caseId, sectionId })
    throw err
  }
}
